"use client";

import { css } from "styled-system/css";
import { es } from "date-fns/locale";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { enumerateDatesInMonth } from "@/lib/slots";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

type Props = {
  calendarMonth: string;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  canPrevMonth: boolean;
  canNextMonth: boolean;
  timeZone: string;
  primaryColor: string;
  minDateStr: string;
  availableDates: Set<string>;
  selectedDate: string;
  onSelectDate: (dateStr: string) => void;
  loadingMonth: boolean;
};

export function BookCalendarGrid({
  calendarMonth,
  onPrevMonth,
  onNextMonth,
  canPrevMonth,
  canNextMonth,
  timeZone,
  primaryColor,
  minDateStr,
  availableDates,
  selectedDate,
  onSelectDate,
  loadingMonth,
}: Props) {
  const days = enumerateDatesInMonth(calendarMonth, timeZone);
  const firstDow = Number(
    formatInTimeZone(fromZonedTime(`${calendarMonth}-01T12:00:00`, timeZone), timeZone, "i"),
  );
  const pad = firstDow - 1;
  const monthTitle = formatInTimeZone(
    fromZonedTime(`${calendarMonth}-01T12:00:00`, timeZone),
    timeZone,
    "LLLL yyyy",
    { locale: es },
  );

  const cells: ({ type: "empty" } | { type: "day"; dateStr: string })[] = [];
  for (let i = 0; i < pad; i++) cells.push({ type: "empty" });
  for (const d of days) cells.push({ type: "day", dateStr: d });
  while (cells.length % 7 !== 0) cells.push({ type: "empty" });

  return (
    <div
      className={css({
        rounded: "xl",
        borderWidth: "1px",
        borderColor: "var(--pp-border, #e2e8f0)",
        bg: "var(--pp-card-bg, white)",
        p: "4",
        minW: { base: "100%", md: "280px" },
        flexShrink: 0,
      })}
    >
      <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "4" })}>
        <button
          type="button"
          aria-label="Mes anterior"
          disabled={!canPrevMonth || loadingMonth}
          onClick={onPrevMonth}
          className={css({
            p: "2",
            rounded: "lg",
            border: "none",
            cursor: canPrevMonth ? "pointer" : "not-allowed",
            opacity: canPrevMonth ? 1 : 0.35,
            bg: "transparent",
            color: "var(--pp-text, #0f172a)",
          })}
        >
          <ChevronLeft size={22} />
        </button>
        <p
          className={css({ fontSize: "sm", fontWeight: "semibold", textTransform: "capitalize" })}
          style={{ color: "var(--pp-text, inherit)" }}
        >
          {loadingMonth ? "…" : monthTitle}
        </p>
        <button
          type="button"
          aria-label="Mes siguiente"
          disabled={!canNextMonth || loadingMonth}
          onClick={onNextMonth}
          className={css({
            p: "2",
            rounded: "lg",
            border: "none",
            cursor: canNextMonth ? "pointer" : "not-allowed",
            opacity: canNextMonth ? 1 : 0.35,
            bg: "transparent",
            color: "var(--pp-text, #0f172a)",
          })}
        >
          <ChevronRight size={22} />
        </button>
      </div>

      <div
        className={css({
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "1",
          textAlign: "center",
        })}
      >
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className={css({ fontSize: "xs", fontWeight: "semibold", py: "2" })}
            style={{ color: "var(--pp-muted, #64748b)" }}
          >
            {w}
          </div>
        ))}
        {cells.map((c, idx) => {
          if (c.type === "empty") {
            return <div key={`e-${idx}`} className={css({ aspectRatio: "1", minH: "9" })} />;
          }
          const { dateStr } = c;
          const past = dateStr < minDateStr;
          const open = availableDates.has(dateStr);
          const selectable = open && !past;
          const selected = dateStr === selectedDate;

          return (
            <button
              key={dateStr}
              type="button"
              disabled={!selectable || loadingMonth}
              onClick={() => selectable && onSelectDate(dateStr)}
              title={
                past
                  ? "Fecha pasada"
                  : !open
                    ? "Sin turnos este día"
                    : `Elegir ${dateStr}`
              }
              className={css({
                aspectRatio: "1",
                minH: "9",
                maxH: "11",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                borderWidth: "1px",
                borderStyle: "solid",
                cursor: selectable && !loadingMonth ? "pointer" : "default",
                transition: "background 0.12s, color 0.12s",
              })}
              style={
                selected
                  ? {
                      backgroundColor: primaryColor,
                      color: "#fff",
                      borderColor: primaryColor,
                    }
                  : selectable
                    ? {
                        backgroundColor: "color-mix(in srgb, var(--pp-page-bg, #f8fafc) 70%, white)",
                        color: "var(--pp-text, #0f172a)",
                        borderColor: "var(--pp-border, #e2e8f0)",
                      }
                    : {
                        backgroundColor: "transparent",
                        color: "var(--pp-muted, #94a3b8)",
                        borderColor: "transparent",
                        opacity: past ? 0.35 : 0.5,
                      }
              }
            >
              {Number(dateStr.slice(8, 10))}
            </button>
          );
        })}
      </div>
    </div>
  );
}
